import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { CatalogSearchQuery, defaultQuery } from "@modules/PsyCatalog/common/searchQuery"
import { FormState } from "@modules/PsyCatalog/common/searchQuery/schema"
import { SlugMeta } from "@modules/PsyCatalog/common/Filters/UrlBuilder"
import { getCurrentPage } from "@modules/PsyCatalog/common/utils"
import {
    City,
    CompetencePage,
    FetchCompetences,
    FetchSearchProfilesResponse,
    MethodPage,
    Psychologist,
    SessionTimeInterval,
} from "@utils/interfaces"
import { LOADING_STATUS } from "@utils/constants/loadingStatus"

export interface CompetencesData {
    groups: FetchCompetences
    list: CompetencePage[]
}

export interface PsyCatalogState {
    // Фильтры
    formOfWork: CatalogSearchQuery["form_of_work"]
    sex: CatalogSearchQuery["sex"]
    maritalStatus: CatalogSearchQuery["marital_status"]
    hasChildren: CatalogSearchQuery["has_children"]
    expressSlots: boolean
    urgentSlots: boolean
    sessionTimeIntervals: SessionTimeInterval[]
    workingWith: CatalogSearchQuery["working_with"]
    price: number[]
    age: number[]
    city: City | undefined
    competences: number[]
    methods: number[]

    // Страницы
    currentPage: number
    pagesCount: number
    profilesCache: Record<number, Psychologist[]>

    // Справочники
    cities: City[]
    methodsList: MethodPage[]
    competencesData: CompetencesData

    // Прочее
    loading: LOADING_STATUS
    searchResult: FetchSearchProfilesResponse
    lastSuccessfullyLoadedState: FormState | undefined
    pageMeta: SlugMeta
    initialized: boolean
}

const defaultMeta: SlugMeta = {
    slug: null,
    value: "",
    kind: "city",
    h1: "Подбор психолога",
}

const initialState: PsyCatalogState = {
    formOfWork: defaultQuery.form_of_work,
    sex: defaultQuery.sex,
    maritalStatus: defaultQuery.marital_status,
    hasChildren: defaultQuery.has_children,
    expressSlots: defaultQuery.express_slots,
    urgentSlots: defaultQuery.urgent_slots,
    sessionTimeIntervals: [],
    workingWith: { ...defaultQuery.working_with },
    price: [defaultQuery.price_from, defaultQuery.price_to],
    age: [defaultQuery.age_from, defaultQuery.age_to],
    city: undefined,
    competences: [],
    methods: [],

    loading: LOADING_STATUS.IDLE,
    searchResult: {
        profiles: [],
        matching_count: 0,
        pages_count: 0,
        pages: [],
        query: { source: { mode: "search" } },
    },
    lastSuccessfullyLoadedState: undefined,

    currentPage: getCurrentPage(),
    pagesCount: 1,
    profilesCache: {},

    cities: [],
    methodsList: [],
    competencesData: { groups: [], list: [] },

    pageMeta: defaultMeta,

    initialized: false,
}

const psyCatalogSlice = createSlice({
    name: "psyCatalog",
    initialState,
    reducers: {
        setFormOfWork(state, action: PayloadAction<CatalogSearchQuery["form_of_work"]>) {
            state.formOfWork = action.payload
        },
        setSex(state, action: PayloadAction<CatalogSearchQuery["sex"]>) {
            state.sex = action.payload
        },
        setMaritalStatus(state, action: PayloadAction<CatalogSearchQuery["marital_status"]>) {
            state.maritalStatus = action.payload
        },
        setHasChildren(state, action: PayloadAction<CatalogSearchQuery["has_children"]>) {
            state.hasChildren = action.payload
        },
        setExpressSlots(state, action: PayloadAction<boolean>) {
            state.expressSlots = action.payload
        },
        toggleExpressSlots(state) {
            state.expressSlots = !state.expressSlots
        },
        setUrgentSlots(state, action: PayloadAction<boolean>) {
            state.urgentSlots = action.payload
        },
        toggleUrgentSlots(state) {
            state.urgentSlots = !state.urgentSlots
        },
        setSessionTimeIntervals(state, action: PayloadAction<SessionTimeInterval[]>) {
            state.sessionTimeIntervals = action.payload
        },
        toggleSessionTimeInterval(state, action: PayloadAction<SessionTimeInterval>) {
            const index = state.sessionTimeIntervals.findIndex(
                (i) => i.begin === action.payload.begin
            )
            if (index >= 0) {
                state.sessionTimeIntervals.splice(index, 1)
            } else {
                state.sessionTimeIntervals.push(action.payload)
            }
        },
        setWorkingWith(state, action: PayloadAction<CatalogSearchQuery["working_with"]>) {
            state.workingWith = action.payload
        },
        toggleWorkingWith(state, action: PayloadAction<keyof CatalogSearchQuery["working_with"]>) {
            state.workingWith[action.payload] = !state.workingWith[action.payload]
        },
        setPrice(state, action: PayloadAction<number[]>) {
            state.price = action.payload
        },
        setAge(state, action: PayloadAction<number[]>) {
            state.age = action.payload
        },
        setCity(state, action: PayloadAction<City | undefined>) {
            state.city = action.payload
        },
        setCompetences(state, action: PayloadAction<number[]>) {
            state.competences = action.payload
        },
        toggleCompetence(state, action: PayloadAction<number>) {
            const index = state.competences.indexOf(action.payload)
            if (index >= 0) {
                state.competences.splice(index, 1)
            } else {
                state.competences.push(action.payload)
            }
        },
        setMethods(state, action: PayloadAction<number[]>) {
            state.methods = action.payload
        },
        toggleMethod(state, action: PayloadAction<number>) {
            const index = state.methods.indexOf(action.payload)
            if (index >= 0) {
                state.methods.splice(index, 1)
            } else {
                state.methods.push(action.payload)
            }
        },
        setLoading(state, action: PayloadAction<LOADING_STATUS>) {
            state.loading = action.payload
        },
        setSearchResult(state, action: PayloadAction<FetchSearchProfilesResponse>) {
            state.searchResult = action.payload
        },
        setSearchResultProfiles(state, action: PayloadAction<Psychologist[]>) {
            state.searchResult.profiles = action.payload
        },
        setLastSuccessfullyLoadedState(state, action: PayloadAction<FormState | undefined>) {
            state.lastSuccessfullyLoadedState = action.payload
        },
        setCurrentPage(state, action: PayloadAction<number>) {
            const page = action.payload
            if (page > 0 && page <= state.pagesCount) {
                state.currentPage = page
            }
        },
        setPagesCount(state, action: PayloadAction<number>) {
            state.pagesCount = action.payload
        },
        setProfilesCache(state, action: PayloadAction<Record<number, Psychologist[]>>) {
            state.profilesCache = action.payload
        },
        addToProfilesCache(
            state,
            action: PayloadAction<{ pageIndex: number; profiles: Psychologist[] }>
        ) {
            state.profilesCache[action.payload.pageIndex] = action.payload.profiles
        },
        setCities(state, action: PayloadAction<City[]>) {
            state.cities = action.payload
        },
        setMethodsList(state, action: PayloadAction<MethodPage[]>) {
            state.methodsList = action.payload
        },
        setCompetencesData(state, action: PayloadAction<CompetencesData>) {
            state.competencesData = action.payload
        },
        setPageMeta(state, action: PayloadAction<SlugMeta>) {
            state.pageMeta = action.payload
        },
        setInitialized(state, action: PayloadAction<boolean>) {
            state.initialized = action.payload
        },
    },
})

export const psyCatalogActions = psyCatalogSlice.actions
export const psyCatalogReducer = psyCatalogSlice.reducer
